import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  User,
  Bell,
  Shield,
  Server,
  Palette,
  Save,
  Eye,
  EyeOff,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useSearchParams } from 'react-router-dom'
import api from '@/lib/api'
import toast from 'react-hot-toast'

type SettingsTab = 'profile' | 'notifications' | 'security' | 'server' | 'branding'

const NOTIFICATION_SETTINGS_KEY = 'pitstop.notification-settings'
const SERVER_SETTINGS_KEY = 'pitstop.server-settings'
const BRANDING_SETTINGS_KEY = 'pitstop.branding-settings'
const AVATAR_STORAGE_KEY = 'pitstop.profile-avatar-preview'
const BRAND_LOGO_STORAGE_KEY = 'pitstop.brand-logo-preview'

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  const tabs = [
    { id: 'profile' as const, name: 'Profile', icon: User },
    { id: 'notifications' as const, name: 'Notifications', icon: Bell },
    { id: 'security' as const, name: 'Security', icon: Shield },
    { id: 'server' as const, name: 'Server', icon: Server },
    { id: 'branding' as const, name: 'Branding', icon: Palette },
  ]

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && tabs.some((candidate) => candidate.id === tab)) {
      setActiveTab(tab as SettingsTab)
    }
  }, [searchParams])

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-sc-text-primary">Settings</h1>
        <p className="text-sc-text-muted mt-1">
          Manage your account and application settings
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-64 flex-shrink-0">
          <div className="card">
            <div className="p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-left transition-colors',
                    activeTab === tab.id
                      ? 'bg-sc-primary/10 text-sc-primary'
                      : 'text-sc-text-secondary hover:text-sc-text-primary hover:bg-sc-bg-light'
                  )}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1">
          {activeTab === 'profile' && <ProfileSettings />}
          {activeTab === 'notifications' && <NotificationSettings />}
          {activeTab === 'security' && <SecuritySettings />}
          {activeTab === 'server' && <ServerSettings />}
          {activeTab === 'branding' && <BrandingSettings />}
        </div>
      </div>
    </div>
  )
}

function ProfileSettings() {
  const { user, updateUser } = useAuthStore()
  const [name, setName] = useState(user?.name || '')
  const [email, setEmail] = useState(user?.email || '')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const saved = localStorage.getItem(AVATAR_STORAGE_KEY)
    if (saved) {
      setAvatarPreview(saved)
    }
  }, [])

  const profileMutation = useMutation({
    mutationFn: async (data: { name: string; email: string }) => {
      const response = await api.patch(`/users/${user!.id}`, data)
      return response.data
    },
    onSuccess: (data) => {
      updateUser({ name: data.name, email: data.email, avatarUrl: avatarPreview || user?.avatarUrl || null })
      toast.success('Profile updated')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile')
    },
  })

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    if (!email.trim()) {
      toast.error('Email is required')
      return
    }
    profileMutation.mutate({ name: name.trim(), email: email.trim() })
  }

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Avatar must be under 2MB')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null
      setAvatarPreview(result)
      if (result) {
        localStorage.setItem(AVATAR_STORAGE_KEY, result)
        updateUser({ avatarUrl: result })
      }
      toast.success('Avatar preview updated')
    }
    reader.readAsDataURL(file)
  }

  const isDirty = name !== (user?.name || '') || email !== (user?.email || '')

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-sc-text-primary">Profile Settings</h2>
        <p className="text-sm text-sc-text-muted mt-1">
          Update your personal information
        </p>
      </div>
      <div className="card-body space-y-6">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-sc-primary/20 flex items-center justify-center text-sc-primary font-bold text-2xl overflow-hidden">
            {avatarPreview || user?.avatarUrl ? (
              <img src={avatarPreview || user?.avatarUrl || ''} alt={user?.name || 'Avatar'} className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0) || 'U'
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button className="btn-secondary py-1.5 text-sm" onClick={() => fileInputRef.current?.click()}>
              Change Avatar
            </button>
            <p className="text-xs text-sc-text-muted mt-2">
              JPG, PNG or GIF. Max 2MB.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-sc-text-secondary mb-2">Full Name</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-sc-text-secondary mb-2">Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Role</label>
          <input type="text" value={user?.role || ''} disabled className="input opacity-60 cursor-not-allowed capitalize" />
          <p className="text-xs text-sc-text-muted mt-1">Contact an admin to change your role.</p>
        </div>

        <div className="flex justify-end pt-4 border-t border-sc-border-dark">
          <button
            onClick={handleSave}
            disabled={profileMutation.isPending || !isDirty}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {profileMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {profileMutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

function NotificationSettings() {
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [sessionAlerts, setSessionAlerts] = useState(true)
  const [deviceAlerts, setDeviceAlerts] = useState(true)
  const [weeklyReport, setWeeklyReport] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      setEmailNotifications(parsed.emailNotifications)
      setSessionAlerts(parsed.sessionAlerts)
      setDeviceAlerts(parsed.deviceAlerts)
      setWeeklyReport(parsed.weeklyReport)
    }
  }, [])

  const handleSave = () => {
    localStorage.setItem(
      NOTIFICATION_SETTINGS_KEY,
      JSON.stringify({ emailNotifications, sessionAlerts, deviceAlerts, weeklyReport })
    )
    toast.success('Notification settings saved on this device')
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-sc-text-primary">Notification Settings</h2>
        <p className="text-sm text-sc-text-muted mt-1">Configure how you receive notifications</p>
      </div>
      <div className="card-body space-y-4">
        <ToggleOption label="Email Notifications" description="Receive important updates via email" checked={emailNotifications} onChange={setEmailNotifications} />
        <ToggleOption label="Session Alerts" description="Get notified when a session starts or ends" checked={sessionAlerts} onChange={setSessionAlerts} />
        <ToggleOption label="Device Alerts" description="Get notified when devices go online/offline" checked={deviceAlerts} onChange={setDeviceAlerts} />
        <ToggleOption label="Weekly Report" description="Receive a weekly summary of activity" checked={weeklyReport} onChange={setWeeklyReport} />
        <div className="flex justify-end pt-4 border-t border-sc-border-dark">
          <button className="btn-primary" onClick={handleSave}>
            <Save className="w-4 h-4" />
            Save Notifications
          </button>
        </div>
      </div>
    </div>
  )
}

function SecuritySettings() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [twoFactor, setTwoFactor] = useState(false)

  const passwordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const response = await api.post('/auth/change-password', data)
      return response.data
    },
    onSuccess: () => {
      toast.success('Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to change password')
    },
  })

  const handleChangePassword = () => {
    if (!currentPassword) {
      toast.error('Current password is required')
      return
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    passwordMutation.mutate({ currentPassword, newPassword })
  }

  const passwordStrength = (() => {
    if (!newPassword) return null
    if (newPassword.length < 8) return { label: 'Too short', color: 'bg-sc-accent-red', width: 'w-1/4' }
    if (newPassword.length < 12) return { label: 'Weak', color: 'bg-sc-accent-orange', width: 'w-2/4' }
    if (/[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^A-Za-z0-9]/.test(newPassword)) {
      return { label: 'Strong', color: 'bg-sc-accent-green', width: 'w-full' }
    }
    return { label: 'Moderate', color: 'bg-sc-accent-yellow', width: 'w-3/4' }
  })()

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-sc-text-primary">Change Password</h2>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="block text-sm font-medium text-sc-text-secondary mb-2">Current Password</label>
            <div className="relative">
              <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="input pr-10" autoComplete="current-password" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sc-text-muted hover:text-sc-text-primary">
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-sc-text-secondary mb-2">New Password</label>
            <div className="relative">
              <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="input pr-10" autoComplete="new-password" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-sc-text-muted hover:text-sc-text-primary">
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {passwordStrength && (
              <div className="mt-2">
                <div className="h-1 w-full bg-sc-bg-light rounded-full overflow-hidden">
                  <div className={cn('h-full rounded-full transition-all', passwordStrength.color, passwordStrength.width)} />
                </div>
                <p className="text-xs text-sc-text-muted mt-1">{passwordStrength.label}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-sc-text-secondary mb-2">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={cn('input', confirmPassword && newPassword !== confirmPassword && 'border-sc-accent-red/50 focus:ring-sc-accent-red/30')} autoComplete="new-password" />
            {confirmPassword && newPassword !== confirmPassword && <p className="text-xs text-sc-accent-red mt-1">Passwords do not match</p>}
          </div>

          <button onClick={handleChangePassword} disabled={passwordMutation.isPending || !currentPassword || !newPassword || newPassword !== confirmPassword} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {passwordMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {passwordMutation.isPending ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-sc-text-primary">Two-Factor Authentication</h2>
        </div>
        <div className="card-body">
          <ToggleOption label="Enable 2FA" description="Add an extra layer of security to your account (coming soon)" checked={twoFactor} onChange={setTwoFactor} disabled />
        </div>
      </div>
    </div>
  )
}

function ServerSettings() {
  const [relayServerUrl, setRelayServerUrl] = useState('relay.simcoaches.com')
  const [relayServerPort, setRelayServerPort] = useState('21117')
  const [apiServerPort, setApiServerPort] = useState('21118')

  useEffect(() => {
    const saved = localStorage.getItem(SERVER_SETTINGS_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      setRelayServerUrl(parsed.relayServerUrl || 'relay.simcoaches.com')
      setRelayServerPort(parsed.relayServerPort || '21117')
      setApiServerPort(parsed.apiServerPort || '21118')
    }
  }, [])

  const handleSave = () => {
    localStorage.setItem(SERVER_SETTINGS_KEY, JSON.stringify({ relayServerUrl, relayServerPort, apiServerPort }))
    toast.success('Server settings saved on this device')
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-sc-text-primary">Server Configuration</h2>
        <p className="text-sm text-sc-text-muted mt-1">Configure your relay server settings</p>
      </div>
      <div className="card-body space-y-4">
        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Relay Server URL</label>
          <input type="text" value={relayServerUrl} onChange={(e) => setRelayServerUrl(e.target.value)} className="input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Relay Server Port</label>
          <input type="text" value={relayServerPort} onChange={(e) => setRelayServerPort(e.target.value)} className="input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">API Server Port</label>
          <input type="text" value={apiServerPort} onChange={(e) => setApiServerPort(e.target.value)} className="input" />
        </div>

        <div className="pt-4 border-t border-sc-border-dark">
          <h3 className="font-medium text-sc-text-primary mb-4">Server Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-sc-bg-light">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-sc-accent-green" />
                <span className="text-sm font-medium text-sc-text-primary">Relay Server</span>
              </div>
              <p className="text-xs text-sc-text-muted">Configured for {relayServerUrl}:{relayServerPort}</p>
            </div>
            <div className="p-4 rounded-lg bg-sc-bg-light">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-sc-accent-green" />
                <span className="text-sm font-medium text-sc-text-primary">API Server</span>
              </div>
              <p className="text-xs text-sc-text-muted">Configured for port {apiServerPort}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-sc-border-dark">
          <button className="btn-primary" onClick={handleSave}>
            <Save className="w-4 h-4" />
            Save Server Settings
          </button>
        </div>
      </div>
    </div>
  )
}

function BrandingSettings() {
  const [companyName, setCompanyName] = useState('Sim Coaches')
  const [primaryColor, setPrimaryColor] = useState('#6366f1')
  const [supportEmail, setSupportEmail] = useState('support@simcoaches.com')
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const saved = localStorage.getItem(BRANDING_SETTINGS_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      setCompanyName(parsed.companyName || 'Sim Coaches')
      setPrimaryColor(parsed.primaryColor || '#6366f1')
      setSupportEmail(parsed.supportEmail || 'support@simcoaches.com')
    }
    const savedLogo = localStorage.getItem(BRAND_LOGO_STORAGE_KEY)
    if (savedLogo) {
      setLogoPreview(savedLogo)
    }
  }, [])

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : null
      setLogoPreview(result)
      if (result) {
        localStorage.setItem(BRAND_LOGO_STORAGE_KEY, result)
      }
      toast.success('Logo preview updated')
    }
    reader.readAsDataURL(file)
  }

  const handleSave = () => {
    localStorage.setItem(BRANDING_SETTINGS_KEY, JSON.stringify({ companyName, primaryColor, supportEmail }))
    toast.success('Branding settings saved on this device')
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="font-semibold text-sc-text-primary">Branding Settings</h2>
        <p className="text-sm text-sc-text-muted mt-1">Customize the look and feel of your remote support platform</p>
      </div>
      <div className="card-body space-y-6">
        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Company Name</label>
          <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="input" />
        </div>

        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Logo</label>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg bg-sc-bg-light flex items-center justify-center overflow-hidden">
              {logoPreview ? <img src={logoPreview} alt="Brand logo" className="w-full h-full object-cover" /> : <Palette className="w-8 h-8 text-sc-text-muted" />}
            </div>
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" onChange={handleLogoUpload} />
            <button className="btn-secondary py-1.5 text-sm" onClick={() => fileInputRef.current?.click()}>Upload Logo</button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Primary Color</label>
          <div className="flex items-center gap-3">
            <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" />
            <input type="text" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="input w-32" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-sc-text-secondary mb-2">Support Email</label>
          <input type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="input" />
        </div>

        <div className="flex justify-end pt-4 border-t border-sc-border-dark">
          <button className="btn-primary" onClick={handleSave}>
            <Save className="w-4 h-4" />
            Save Branding
          </button>
        </div>
      </div>
    </div>
  )
}

interface ToggleOptionProps {
  label: string
  description: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function ToggleOption({ label, description, checked, onChange, disabled }: ToggleOptionProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-sc-border-dark last:border-0">
      <div>
        <p className={cn('font-medium text-sc-text-primary', disabled && 'opacity-50')}>{label}</p>
        <p className={cn('text-sm text-sc-text-muted', disabled && 'opacity-50')}>{description}</p>
      </div>
      <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={cn(
          'relative w-11 h-6 rounded-full transition-colors',
          checked ? 'bg-sc-primary' : 'bg-sc-bg-light',
          disabled && 'opacity-40 cursor-not-allowed'
        )}
      >
        <span className={cn('absolute top-1 w-4 h-4 rounded-full bg-white transition-transform', checked ? 'left-6' : 'left-1')} />
      </button>
    </div>
  )
}
